import { Component, OnInit, AfterViewInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';

import { Router } from '@angular/router';
import { NewCustomerDialogComponent } from '../new-customer-dialog/new-customer-dialog.component';
import { NewCustomerDialogResult } from '../../../interfaces/new-customer-dialog.interface';
import { CustomersService } from '../../../services/customers.service';
import { CustomerListItem } from '../../../interfaces/customer-service.interface';
import { AddNewButton } from '../../../shared/components/add-new-button/add-new-button';
import { CallCenterChipComponent } from '../../../shared/components/call-center-chip/call-center-chip.component';

// Interfész átmozgatva a customers.service.ts-be az egységesség érdekében

@Component({
  selector: 'app-customers-table',
  imports: [
    MatTableModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatChipsModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTooltipModule,
    AddNewButton,
    CallCenterChipComponent
],
  templateUrl: './customers-table.component.html',
  styleUrl: './customers-table.component.scss'
})
export class CustomersTableComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns: string[] = ['companyInfo', 'officials', 'contacts', 'actions'];
  dataSource = new MatTableDataSource<CustomerListItem>([]);
  
  // Paginátor beállítások
  totalCount = 0;
  pageSize = 25;
  pageIndex = 0;
  pageSizeOptions = [10, 25, 50, 100];
  
  // Loading states
  isLoading = false;
  error: string | null = null;

  constructor(
    private dialog: MatDialog,
    private customersService: CustomersService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}
  ngOnInit(): void {
    // Üres - az adatok betöltését az ngAfterViewInit-ben végezzük
  }

  ngAfterViewInit(): void {
    // Set up custom filter predicate
    this.dataSource.filterPredicate = (data: CustomerListItem, filter: string) => {
      const searchText = filter.toLowerCase();
      
      // Search in company name, officials, phone numbers, emails
      return (
        (data.companyName?.toLowerCase().includes(searchText)) ||
        (data.officials?.some(official => official.toLowerCase().includes(searchText))) ||
        (data.phoneNumbers?.some(phone => phone.toLowerCase().includes(searchText))) ||
        (data.emails?.some(email => email.toLowerCase().includes(searchText))) ||
        (data.taxNumber?.toLowerCase().includes(searchText)) ||
        (data.registrationNumber?.toLowerCase().includes(searchText)) ||
        (data.mainActivity?.toLowerCase().includes(searchText)) ||
        (data.county?.toLowerCase().includes(searchText)) ||
        (data.address?.toLowerCase().includes(searchText))
      );
    };

    this.loadCustomers();
  }

  /**
   * Ügyfelek betöltése az adatbázisból
   */
  loadCustomers(): void {
    // Aszinkron állapot beállítás a Change Detection hiba elkerülése érdekében
    setTimeout(() => {
      this.isLoading = true;
      this.error = null;
      this.cdr.detectChanges();
    }, 0);

    this.customersService.getCustomers(this.pageIndex + 1, this.pageSize).subscribe({
      next: (response) => {
        console.log('✅ Ügyfelek sikeresen betöltve:', response);
        
        if (response.success) {
          this.dataSource.data = response.data;
          this.totalCount = response.pagination.total;
          this.pageIndex = response.pagination.page - 1; // API 1-based, Material 0-based
          console.log('📊 DataSource frissítve:', this.dataSource.data.length, 'elemmel');
        } else {
          this.error = 'Hiba az ügyfelek betöltése során';
          this.showError('Hiba az ügyfelek betöltése során');
        }
        
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Hiba az ügyfelek betöltése során:', error);
        this.error = 'Hiba az ügyfelek betöltése során';
        this.showError('Hiba az ügyfelek betöltése során');
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Paginátor oldal változás kezelése
   */
  onPageChange(event: PageEvent): void {
    console.log('📄 Paginator event:', event);
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadCustomers();
  }

  /**
   * Hibaüzenet megjelenítése
   */
  private showError(message: string): void {
    this.snackBar.open(message, 'Bezárás', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  /**
   * Siker üzenet megjelenítése
   */
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'OK', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  onEdit(customer: CustomerListItem): void {
    console.log('Szerkesztés:', customer);
    // Itt lesz az ügyfél szerkesztés logikája
    this.showSuccess(`${customer.companyName} szerkesztése`);
  }

  onDelete(customer: CustomerListItem): void {
    console.log('Törlés:', customer);
    
    if (confirm(`Biztosan törölni szeretné a következő ügyfelet: ${customer.companyName}?`)) {
      this.customersService.deleteCustomer(customer.id).subscribe({
        next: (response) => {
          console.log('✅ Ügyfél sikeresen törölve:', response);
          this.showSuccess(`${customer.companyName} sikeresen törölve`);
          this.loadCustomers(); // Frissítjük a listát
        },
        error: (error) => {
          console.error('❌ Hiba az ügyfél törlésekor:', error);
          this.showError('Hiba az ügyfél törlésekor');
        }
      });
    }
  }

  onViewDetails(customer: CustomerListItem): void {
    console.log('Részletek:', customer);
    
    // Navigate to customer details page based on the customer type
    if (customer.type === 'company') {
      this.router.navigate(['/company-details', customer.id]);
    } else {
      this.router.navigate(['/customer-details', customer.id]);
    }
  }

  openNewCustomerDialog(): void {
    const dialogRef = this.dialog.open(NewCustomerDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {},
      panelClass: 'crm-dialog'
    });

    dialogRef.afterClosed().subscribe((result: NewCustomerDialogResult) => {
      if (result && result.action === 'save') {
        console.log('Új ügyfél mentve:', result.data);
        
        const response = result.data?.backendResponse;
        if (response && response.success) {
          console.log('✅ Backend válasz:', response);
          this.showSuccess('Új ügyfél sikeresen létrehozva!');
          
          // Táblázat adatainak frissítése
          this.loadCustomers();
        } else if (response && !response.success) {
        }
      }
    });
  }

  /**
   * Meghatározza egy tisztségviselő validStatus értékét az adatbázis alapján
   * @param official A tisztségviselő neve
   * @param customer Az ügyfél objektum, amely tartalmazza az officialsWithStatus információt
   * @returns true (valid/green), false (invalid/red), null (neutral/gray)
   */
  getOfficialValidStatus(official: string, customer: CustomerListItem): boolean | null {
    // Ha van officialsWithStatus információ, azt használjuk
    if (customer.officialsWithStatus && customer.officialsWithStatus.hasOwnProperty(official)) {
      return customer.officialsWithStatus[official];
    }
    
    // Fallback: ha nincs adatbázisból érkező információ, akkor null (neutral)
    return null;
  }

  /**
   * Meghatározza egy elérhetőség validStatus értékét az adatbázis alapján
   * @param contactValue Az elérhetőség értéke (email vagy telefon)
   * @param contactType Az elérhetőség típusa ('email' vagy 'phone')
   * @param customer Az ügyfél objektum, amely tartalmazza az elérhetőségek validstatus információt
   * @returns true (valid/green), false (invalid/red), null (neutral/gray)
   */
  getContactValidStatus(contactValue: string, contactType: 'email' | 'phone', customer: CustomerListItem): boolean | null {
    if (contactType === 'email') {
      // Ha van emailsWithStatus információ, azt használjuk
      if (customer.emailsWithStatus && customer.emailsWithStatus.hasOwnProperty(contactValue)) {
        return customer.emailsWithStatus[contactValue];
      }
    }
    
    if (contactType === 'phone') {
      // Ha van phoneNumbersWithStatus információ, azt használjuk
      if (customer.phoneNumbersWithStatus && customer.phoneNumbersWithStatus.hasOwnProperty(contactValue)) {
        return customer.phoneNumbersWithStatus[contactValue];
      }
    }
    
    // Fallback: ha nincs adatbázisból érkező információ, akkor null (neutral)
    return null;
  }

  /**
   * Adatok szűrése keresési kifejezés alapján
   */
  filterData(searchTerm: string): void {
    this.dataSource.filter = searchTerm.trim().toLowerCase();
    
    // Ha van paginator, akkor az első oldalra ugorjunk a keresés után
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }
}